import { Button } from '@myosh/odin-components';
import { memo, useCallback } from 'react';
import type { ToastContentProps } from 'react-toastify';

interface ConfirmActionToastProps extends ToastContentProps {
  message: string;
  confirmActionLabel: string;
  onConfirmActionClick: () => void;
}

/**
 * A toast component that displays a confirmation message with action buttons.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.message - The confirmation message to display
 * @param {string} props.confirmActionLabel - The label for the confirm action button
 * @param {() => void} props.onConfirmActionClick - Callback function triggered when confirm action is clicked
 * @param {() => void} props.closeToast - Callback function to close the toast
 *
 * @example
 * ```tsx
 * <ConfirmActionToast
 *   message="Are you sure you want to proceed?"
 *   confirmActionLabel="Confirm"
 *   onConfirmActionClick={() => handleConfirm()}
 *   closeToast={() => closeToast()}
 * />
 * ```
 *
 * @returns A toast component with a message and action buttons
 */
const ConfirmActionToast = ({
  message,
  confirmActionLabel,
  onConfirmActionClick,
  closeToast,
}: ConfirmActionToastProps) => {
  const handleUnlink = useCallback(() => {
    onConfirmActionClick();
    closeToast();
  }, [onConfirmActionClick, closeToast]);

  return (
    <div data-cy="confirm-action-toast" className="flex flex-col">
      <div data-cy="confirm-action-toast-message" className="py-4 whitespace-pre-line">
        {message}
      </div>

      <div className="flex flex-row-reverse gap-2">
        <Button type="primary" onClick={handleUnlink}>
          {confirmActionLabel}
        </Button>
        <Button type="text" onClick={closeToast}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default memo(ConfirmActionToast);
